/*
Confirmation modal for bulk vehicle publishing
Shows selected vehicles and asks for user confirmation before proceeding
Autor: Assistant  
Fecha: 2025-01-21
*/
import React from 'react'
import { Dialog, Flex, Text, Button, ScrollArea, Badge, Strong } from '@radix-ui/themes'

import Vehicle from '@/interfaces/Vehicle'
import { useFormatter } from '@/utils/formats'

interface BulkPublishConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  selectedVehicles: Vehicle[]
  platform: 'mercadolibre' | 'chileautos' | 'yapo' | 'all'
  getBranchName: (branchId: string | null | undefined) => string
}

const BulkPublishConfirmationModal: React.FC<BulkPublishConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedVehicles,
  platform,
  getBranchName,
}) => {
  const { formatCurrency } = useFormatter()

  // Un vehículo ya publicado en la plataforma de destino no se publica de nuevo:
  // la API encuentra el identificador existente y republica, sobrescribiendo el
  // aviso que está en línea. La confirmación no lo distinguía, así que una
  // selección de la pantalla de publicados sobrescribía avisos sin avisar.
  const isPublishedOn = (vehicle: Vehicle, target: typeof platform): boolean => {
    switch (target) {
      case 'mercadolibre':
        // El identificador es lo que hace que la API republique en vez de crear.
        return !!vehicle.external_meli?.identifier
      case 'chileautos':
        return !!vehicle.external_chileautos?.identifier
      case 'yapo':
        return !!vehicle.external_yapo?.publishedAt
      case 'all':
        return (['mercadolibre', 'chileautos', 'yapo'] as const).some((p) => isPublishedOn(vehicle, p))
      default:
        return false
    }
  }

  const alreadyPublished = selectedVehicles.filter((vehicle) => isPublishedOn(vehicle, platform))
  const brandNew = selectedVehicles.length - alreadyPublished.length

  const getPlatformText = () => {
    switch (platform) {
      case 'mercadolibre':
        return 'MercadoLibre'
      case 'chileautos':
        return 'ChileAutos'
      case 'yapo':
        return 'Yapo'
      case 'all':
        return 'Todas las Plataformas'
      default:
        return platform
    }
  }

  const getPlatformBadgeColor = () => {
    switch (platform) {
      case 'mercadolibre':
        return 'yellow'
      case 'chileautos':
        return 'blue'
      case 'yapo':
        return 'orange'
      case 'all':
        return 'purple'
      default:
        return 'gray'
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 700, maxHeight: '85vh' }}>
        <Dialog.Title>Confirmar Publicación Masiva</Dialog.Title>

        <Flex direction="column" gap="4" mt="4">
          {/* Header Info */}
          <Flex
            direction="column"
            gap="2"
            p="3"
            style={{ backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          >
            <Flex align="center" justify="between">
              <Text size="3" weight="medium">
                Plataforma de destino:
              </Text>
              <Badge size="2" color={getPlatformBadgeColor()}>
                {getPlatformText()}
              </Badge>
            </Flex>
            <Flex align="center" justify="between">
              <Text size="3" weight="medium">
                Vehículos seleccionados:
              </Text>
              <Strong>{selectedVehicles.length}</Strong>
            </Flex>
            {alreadyPublished.length > 0 && (
              <>
                <Flex align="center" justify="between">
                  <Text size="2" color="gray">
                    Publicaciones nuevas:
                  </Text>
                  <Strong>{brandNew}</Strong>
                </Flex>
                <Flex align="center" justify="between">
                  <Text size="2" color="orange" weight="medium">
                    Ya publicados (se sobrescribirán):
                  </Text>
                  <Strong>{alreadyPublished.length}</Strong>
                </Flex>
              </>
            )}
          </Flex>

          {/* Confirmation Message */}
          <Flex
            direction="column"
            gap="2"
            p="3"
            style={{ backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #f59e0b' }}
          >
            <Text size="3" weight="medium" color="orange">
              ⚠️ Confirmación requerida
            </Text>
            <Text size="2">
              {platform === 'all'
                ? `Se publicarán ${selectedVehicles.length} vehículos en MercadoLibre, ChileAutos y Yapo simultáneamente.`
                : `Se publicarán ${selectedVehicles.length} vehículos en ${getPlatformText()}.`}
            </Text>
            {alreadyPublished.length > 0 && (
              <Text size="2" weight="medium" color="orange">
                {alreadyPublished.length === 1
                  ? `1 de los vehículos seleccionados ya está publicado: su aviso en línea se sobrescribirá con los datos actuales.`
                  : `${alreadyPublished.length} de los vehículos seleccionados ya están publicados: sus avisos en línea se sobrescribirán con los datos actuales.`}
              </Text>
            )}
            <Text size="2" color="gray">
              Esta acción no se puede deshacer. Revise la lista de vehículos antes de continuar.
            </Text>
          </Flex>

          {/* Vehicle List */}
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium" color="gray">
              Vehículos a publicar:
            </Text>
            <ScrollArea style={{ height: 300, border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <Flex direction="column" gap="1" p="2">
                {selectedVehicles.map((vehicle, index) => (
                  <Flex
                    key={vehicle._id}
                    align="center"
                    justify="between"
                    p="3"
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <Flex direction="column" gap="1">
                      <Flex align="center" gap="2">
                        <Strong>{vehicle.plate}</Strong>
                        <Text size="2">
                          {vehicle.brand} {vehicle.model}
                        </Text>
                        {isPublishedOn(vehicle, platform) && (
                          <Badge size="1" color="orange">
                            Ya publicado — se sobrescribe
                          </Badge>
                        )}
                      </Flex>
                      <Flex align="center" gap="3">
                        <Text size="1" color="gray">
                          {vehicle.version}
                        </Text>
                        <Text size="1" color="gray">
                          Año: {vehicle.year || '-'}
                        </Text>
                        <Text size="1" color="gray">
                          Sucursal: {getBranchName(vehicle.branch)}
                        </Text>
                      </Flex>
                    </Flex>
                    <Flex direction="column" align="end" gap="1">
                      <Text size="2" weight="medium">
                        {formatCurrency(vehicle.price, '-')}
                      </Text>
                      <Text size="1" color="gray">
                        #{index + 1}
                      </Text>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </ScrollArea>
          </Flex>

          {/* Action Buttons */}
          <Flex gap="3" justify="end" pt="3">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={onConfirm} color={getPlatformBadgeColor()}>
              {alreadyPublished.length > 0 ? 'Confirmar, Publicar y Republicar' : 'Confirmar y Publicar'}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default BulkPublishConfirmationModal
